// src/components/layout/Topbar.jsx
// Rediseño Fase 3 — barra superior DELGADA. La navegación y el badge/sockets de
// "Atención a Clientes" se movieron al Sidebar. Aquí queda: hamburguesa (abrir
// sidebar en móvil), búsqueda, break, campana, tema y menú de usuario.
import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import NotificationBell from '../NotificationBell';
import UserProfileModal from '../UserProfileModal';
import BreakControl from './BreakControl';
import socket from '../../services/socket';
import {
    requestPermission as requestNotifPermission,
    showNotification as showOsNotification,
    isTabHidden,
    hasBeenAsked as notifHasBeenAsked,
} from '../../services/notifications';


// ── Iconos SVG inline ────────────────────────────────────────────────────────
const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    search:   <Icon size={15}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Icon>,
    sun:      <Icon><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></Icon>,
    moon:     <Icon><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></Icon>,
    menu:     <Icon><path d="M3 12h18M3 6h18M3 18h18" /></Icon>,
    user:     <Icon size={14}><circle cx="12" cy="8" r="4" /><path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" /></Icon>,
    logout:   <Icon size={14}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></Icon>,
    layout:   <Icon size={14}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></Icon>,
};


// ── Avatar helpers ───────────────────────────────────────────────────────────
const AVATAR_PALETTE = [
    '#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed',
    '#0891b2', '#db2777', '#ea580c', '#65a30d', '#0d9488',
];
function nameToColor(name) {
    if (!name) return '#9ca3af';
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}
function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[1][0]).toUpperCase();
}


// ── Scoped CSS ───────────────────────────────────────────────────────────────
const TB_CSS = `
.tb-root {
    --tb-bg-from: var(--brand-900, #0a2540);
    --tb-bg-to: var(--brand-800, #103a6b);
    --tb-text: #fff;
    --tb-text-muted: rgba(255,255,255,.72);

    position: sticky; top: 0; z-index: 1030;
    background: linear-gradient(180deg, var(--tb-bg-from), var(--tb-bg-to));
    color: var(--tb-text);
    box-shadow: 0 2px 12px rgba(5, 25, 70, .22);
    font-family: var(--font-sans);
    -webkit-font-smoothing: antialiased;
}
.tb-root * { box-sizing: border-box; }

.tb-inner {
    display: flex; align-items: center; gap: 12px;
    padding: 10px 22px;
}

/* Brand (solo móvil — en desktop la marca vive en el sidebar) */
.tb-brand {
    display: none; align-items: center; gap: 10px;
    color: #fff; text-decoration: none; font-weight: 700; letter-spacing: .01em;
    padding: 4px 6px; border-radius: 8px;
}
.tb-brand img { height: 24px; width: auto; display: block; }
.tb-brand-name { font-size: 14px; }

.tb-spacer { flex: 1; min-width: 0; }

/* Dropdown menu */
.tb-dropdown { position: relative; }
.tb-menu {
    position: absolute; top: calc(100% + 6px); right: 0;
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: 12px;
    box-shadow: var(--shadow-2);
    min-width: 240px; padding: 6px; z-index: 1040;
    animation: tb-menu-pop .14s ease-out both;
}
@keyframes tb-menu-pop {
    from { opacity: 0; transform: translateY(-4px) scale(.98); }
    to   { opacity: 1; transform: none; }
}
.tb-menu-item {
    display: flex; align-items: center; gap: 10px;
    width: 100%; padding: 8px 12px;
    border-radius: 8px; border: none; background: transparent;
    color: var(--c-text-1); text-decoration: none;
    font-size: 13.5px; text-align: left; cursor: pointer;
    font-family: inherit; transition: background .12s;
}
.tb-menu-item:hover { background: var(--c-msg-bg); color: var(--c-text-1); }
.tb-menu-item.danger { color: #dc2626; }
.tb-menu-item.danger:hover { background: var(--c-priority-urgent-bg); }
.tb-menu-divider { height: 1px; background: var(--c-card-header-border); margin: 6px 4px; }

/* Search input pill */
.tb-search {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,.08);
    border: 1px solid rgba(255,255,255,.14);
    color: var(--tb-text-muted);
    border-radius: 999px; padding: 6px 12px 6px 11px;
    font-size: 13px; cursor: pointer; font-family: inherit;
    transition: background .15s, border-color .15s;
    min-width: 220px; max-width: 320px;
}
.tb-search:hover { background: rgba(255,255,255,.14); border-color: rgba(255,255,255,.22); color: #fff; }
.tb-search .tb-search-label { flex: 1; text-align: left; }
.tb-search-kbd {
    display: inline-flex; align-items: center;
    background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.14);
    border-radius: 5px; padding: 1px 6px; font-size: 10px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    color: rgba(255,255,255,.8); letter-spacing: .3px;
}

/* Icon button */
.tb-icon-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px;
    background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.14);
    color: var(--tb-text); border-radius: 999px; cursor: pointer;
    transition: background .15s, border-color .15s; font-family: inherit; flex-shrink: 0;
}
.tb-icon-btn:hover { background: rgba(255,255,255,.16); border-color: rgba(255,255,255,.24); }

/* User chip */
.tb-user-chip {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.14);
    color: #fff; border-radius: 999px; padding: 4px 11px 4px 4px;
    cursor: pointer; transition: background .15s, border-color .15s;
    font-family: inherit; max-width: 200px;
}
.tb-user-chip:hover { background: rgba(255,255,255,.16); border-color: rgba(255,255,255,.24); }
.tb-user-avatar {
    width: 28px; height: 28px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-weight: 700; font-size: 11px;
    box-shadow: 0 0 0 2px rgba(255,255,255,.18); flex-shrink: 0;
}
.tb-user-name {
    font-size: 12.5px; font-weight: 600; max-width: 140px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.tb-actions { display: flex; align-items: center; gap: 8px; }
/* Hamburguesa siempre visible: en escritorio oculta/muestra el sidebar; en
   móvil abre el overlay. */
.tb-hamburger { display: inline-flex; }

/* Búsqueda como ícono cuando no cabe la pastilla (tablet/móvil) */
.tb-search-icon { display: none; }
@media (max-width: 1100px) { .tb-search { display: none; } .tb-search-icon { display: inline-flex; } }
@media (max-width: 992px) {
    .tb-brand { display: inline-flex; }
}
@media (max-width: 900px) { .tb-user-name { display: none; } .tb-user-chip { padding: 4px; } }
/* Teléfono: solo logo (sin "Caprepa Desk"), menos padding y gaps para que
   quepan hamburguesa, pausa, búsqueda, campana, tema y avatar en 360-390px. */
@media (max-width: 520px) {
    .tb-inner { padding: 8px 10px; gap: 6px; }
    .tb-actions { gap: 5px; }
    .tb-brand { padding: 4px 2px; }
    .tb-brand-name { display: none; }
    .tb-brand img { height: 22px; }
}

.tb-actions .btn-outline-light { border-color: rgba(255,255,255,.18) !important; }
.tb-actions .btn-outline-light:hover { background: rgba(255,255,255,.16) !important; }
.tb-actions .dropdown-menu { font-family: inherit; }
`;


function useClickOutside(ref, onOutside, when = true) {
    useEffect(() => {
        if (!when) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [ref, onOutside, when]);
}


export default function Topbar({ theme, onToggleTheme, onOpenSearch, onToggleSidebar, onOpenShortcuts }) {
    const { user, logout } = useContext(AuthContext);
    const location = useLocation();
    const [profileOpen, setProfileOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userRef = useRef(null);

    useClickOutside(userRef, () => setUserMenuOpen(false), userMenuOpen);

    // Cerrar el menú de usuario al cambiar de ruta
    useEffect(() => { setUserMenuOpen(false); }, [location.pathname]);

    const myUserId = user?.id || user?.userId || user?._id || null;
    const isAdmin  = user?.role === 'admin';
    const isLeader = user?.role === 'leader';
    const isAgent  = user?.role === 'agent';
    const isAnyStaff = isAdmin || isLeader || isAgent;

    // Pedir permiso de notificaciones del SO una sola vez para staff.
    useEffect(() => {
        if (!isAnyStaff) return;
        if (notifHasBeenAsked()) return;
        const t = setTimeout(() => { requestNotifPermission().catch(() => {}); }, 2000);
        return () => clearTimeout(t);
    }, [isAnyStaff]);

    // Tickets escalados al depto del staff (ej: support → DBA). Independiente
    // del badge de Atención (que vive en el Sidebar).
    useEffect(() => {
        if (!isAnyStaff) return undefined;
        const focusTicket = (id) => {
            try { window.focus(); } catch {}
            if (id) window.location.href = `/tickets/${id}`;
        };
        const onEscalated = (payload) => {
            if (!isTabHidden()) return;
            showOsNotification({
                title: `⚠ Ticket #${payload.folio} escalado a tu equipo`,
                body:  `${payload.title || ''}\n${payload.note ? '— ' + payload.note : ''}`.trim(),
                tag:   `ticket-escalated-${payload.ticketId}`,
                requireInteraction: true,
                onClick: () => focusTicket(payload.ticketId),
            });
        };
        socket.on('ticketEscalatedToDept', onEscalated);
        return () => socket.off('ticketEscalatedToDept', onEscalated);
    }, [isAnyStaff]);

    return (
        <>
            <style>{TB_CSS}</style>
            <header className="tb-root">
                <div className="tb-inner">
                    <button
                        type="button"
                        className="tb-hamburger tb-icon-btn"
                        onClick={onToggleSidebar}
                        aria-label="Abrir menú"
                    >
                        {I.menu}
                    </button>

                    <Link to="/dashboard" className="tb-brand">
                        <img src="/caprepa.svg" alt="Caprepa"
                             onError={({ currentTarget }) => { currentTarget.style.display = 'none'; }} />
                        <span className="tb-brand-name">Caprepa Desk</span>
                    </Link>

                    <div className="tb-spacer" />

                    <div className="tb-actions">
                        <button
                            type="button"
                            className="tb-search"
                            onClick={onOpenSearch}
                            aria-label="Buscar (Ctrl+K)"
                            title="Buscar (Ctrl+K)"
                        >
                            {I.search}
                            <span className="tb-search-label">Buscar…</span>
                            <span className="tb-search-kbd">Ctrl K</span>
                        </button>
                        {/* Misma búsqueda como ícono en pantallas chicas (la pastilla se oculta) */}
                        <button
                            type="button"
                            className="tb-icon-btn tb-search-icon"
                            onClick={onOpenSearch}
                            aria-label="Buscar"
                            title="Buscar"
                        >
                            {I.search}
                        </button>

                        <BreakControl />
                        <NotificationBell />

                        <button
                            type="button"
                            className="tb-icon-btn"
                            onClick={onToggleTheme}
                            aria-label={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                            title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                        >
                            {theme === 'dark' ? I.sun : I.moon}
                        </button>

                        <div className="tb-dropdown" ref={userRef}>
                            <button
                                type="button"
                                className="tb-user-chip"
                                onClick={() => setUserMenuOpen(v => !v)}
                                aria-label={`Menú de usuario, ${user?.name || 'usuario'}`}
                                aria-expanded={userMenuOpen}
                            >
                                <div className="tb-user-avatar" style={{ background: nameToColor(user?.name) }} aria-hidden="true">
                                    {getInitials(user?.name)}
                                </div>
                                <span className="tb-user-name">{user?.name || 'Usuario'}</span>
                            </button>
                            {userMenuOpen && (
                                <div className="tb-menu">
                                    <Link to="/dashboard" className="tb-menu-item">{I.layout} Dashboard</Link>
                                    {myUserId && (
                                        <button
                                            type="button"
                                            className="tb-menu-item"
                                            onClick={() => { setProfileOpen(true); setUserMenuOpen(false); }}
                                        >
                                            {I.user} Mi perfil
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="tb-menu-item"
                                        onClick={() => { onOpenShortcuts?.(); setUserMenuOpen(false); }}
                                    >
                                        ⌨️ Atajos de teclado <span className="tb-search-kbd" style={{ marginLeft: 'auto' }}>?</span>
                                    </button>
                                    <div className="tb-menu-divider" />
                                    <button type="button" className="tb-menu-item danger" onClick={logout}>
                                        {I.logout} Cerrar sesión
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <UserProfileModal
                userId={profileOpen ? myUserId : null}
                onClose={() => setProfileOpen(false)}
            />
        </>
    );
}
